import { makeAutoObservable } from "mobx";
import { DateRange, RDate } from "../../core";
import { Availability } from "../Availability";
import { Delivery } from "../Delivery";
import { DeliveryAddress } from "../DeliveryAddress";
import { FulfillmentInstructions } from "../FulfillmentInstructions";
import { Money } from "../Money";
import { OrderReceiver } from "../OrderReceiver";
import { Price } from "../Price";
import { PromotionScheme } from "../PromotionScheme";
import { PurchaseOrderPayload } from "../PurchaseOrderPayload/PurchaseOrderPayload";
import { PurchaseOrderValidationPrice } from "./PurchaseOrderValidationPrice";
/**
 * Represents an order from the perspective of a buyer.
 * It contains items added by user
 */
export class PurchaseOrder {
    constructor(params) {
        this.restaurant = params.restaurant;
        this.origin = params.origin;
        this.payload = params.payload;
        this.orderReceiver = params.orderReceiver;
        this.delivery = params.delivery;
        this.fulfillmentInstructions = params.fulfillmentInstructions;
        this.promotionScheme = params.promotionScheme;
        // TODO: get "now" from the scope?
        this.now = RDate.now(this.restaurant.timezone);
        makeAutoObservable(this);
    }
    static empty(params) {
        const payload = PurchaseOrderPayload.empty(params.restaurant.menu);
        const fulfillmentInstructions = FulfillmentInstructions.create({
            restaurant: params.restaurant,
            dineInLocationNumber: params.dineInLocationNumber,
            deliveryAddress: DeliveryAddress.empty({
                restaurantId: params.restaurant.id,
                geocodingService: params.geocodingService,
                geolocationService: params.geolocationService,
                country: params.country,
            }),
        });
        return new PurchaseOrder({
            restaurant: params.restaurant,
            origin: params.origin,
            payload,
            orderReceiver: OrderReceiver.empty({
                isQrWaiterOrder: params.isQrWaiterOrder,
                validatePhoneService: params.validatePhoneService,
            }),
            delivery: new Delivery({
                restaurantId: params.restaurant.id,
                geocodingService: params.geocodingService,
                fulfillmentInstructions,
            }),
            fulfillmentInstructions,
            promotionScheme: PromotionScheme.empty({
                initialPromotionTypes: params.restaurant.promotionTypes,
                purchaseOrderPayload: payload,
            }),
        });
    }
    get consents() {
        return this.orderReceiver.consents;
    }
    get contactDetails() {
        return this.orderReceiver.contactDetails;
    }
    get invoiceDetails() {
        return this.orderReceiver.invoiceRequest;
    }
    addOrderLine(orderLine) {
        this.payload.addOrderLine(orderLine);
    }
    get orderLines() {
        return this.payload.orderLines;
    }
    clear() {
        // TODO this.promotionScheme.clear()
        this.payload.clear();
    }
    get numberOfItems() {
        return this.payload.numberOfItems;
    }
    get packaging() {
        return this.restaurant.packingAlgorithm.pack(this.payload.packingItems);
    }
    get packagingPrice() {
        return this.packaging.price;
    }
    get minOrderValue() {
        return this.delivery.minOrderValue;
    }
    get promotionEffectsPrice() {
        return this.promotionScheme.price;
    }
    get priceWithoutDelivery() {
        const packagingPrice = Price.unscoped(this.packagingPrice);
        return this.payload.price
            .add(packagingPrice)
            .add(this.promotionEffectsPrice);
    }
    get validationPrice() {
        const priceWithoutDelivery = this.priceWithoutDelivery.finalValue;
        if (priceWithoutDelivery === null) {
            return null;
        }
        return PurchaseOrderValidationPrice.create({
            deliveryInTotal: this.restaurant.orderingPolicy.deliveryInTotal,
            priceWithoutDelivery,
            deliveryCost: this.delivery.cost,
        });
    }
    get deliveryPrice() {
        if (this.validationPrice === null) {
            return Money.zero();
        }
        if (this.fulfillmentInstructions.fulfillmentMethod === "Delivery") {
            return this.delivery.price(this.validationPrice);
        }
        return Money.zero();
    }
    get price() {
        const deliveryPrice = Price.unscoped(this.deliveryPrice);
        return this.priceWithoutDelivery.add(deliveryPrice);
    }
    get orderValueAvailability() {
        if (this.validationPrice === null) {
            return Availability.unavailable("MissingParameterValues");
        }
        if (this.restaurant.orderingPolicy.validate(this.validationPrice)) {
            return Availability.available();
        }
        return Availability.unavailable("MaximumOrderValueExceeded");
    }
    get deliveryAvailability() {
        if (!this.fulfillmentInstructions.isDelivery) {
            return Availability.available();
        }
        const validationPrice = this.validationPrice;
        if (validationPrice === null) {
            return Availability.unavailable("MissingParameterValues");
        }
        return this.delivery.availability(validationPrice);
    }
    get availability() {
        return Availability.composite([
            this.cartAvailability,
            this.deliveryAvailability,
            this.orderReceiver.availability,
            this.fulfillmentInstructions.availability,
        ]);
    }
    get canPlaceOrder() {
        return this.availability.isAvailable.isDefinitelyTrue;
    }
    get cartAvailability() {
        return Availability.composite([
            Availability.dependent("RestaurantOrderingAvailability", "", this.restaurant.orderingAvailability),
            Availability.dependent("PurchaseOrderPayload", "", this.payload.availability),
            this.orderValueAvailability,
        ]);
    }
    get isReadyForCheckout() {
        return this.cartAvailability.isAvailable.isDefinitelyTrue;
    }
    get checkoutAvailability() {
        return Availability.composite([
            this.orderReceiver.availability,
            this.fulfillmentInstructions.availability,
        ]);
    }
    get isCheckoutFormValid() {
        return this.checkoutAvailability.isAvailable.isDefinitelyTrue;
    }
    get preorderSettings() {
        return this.orderLines.flatMap((orderLine) => orderLine.preorderSettings);
    }
    get isPreorder() {
        return (!this.preorderSettings.isEmpty &&
            !this.preorderSettings.some((preorderSetting) => preorderSetting.tag === "SameDay"));
    }
    // TODO: take into condition: order line schedule
    get availableDateRange() {
        if (!this.isPreorder) {
            return new DateRange(this.now, this.now.endOfDay());
        }
        return this.preorderSettings.reduce((dateRange, preorderSetting) => {
            const preorderDateRange = preorderSetting.dateRange(this.now);
            if (dateRange === null) {
                return preorderDateRange;
            }
            return dateRange.intersection(preorderDateRange);
        }, null);
    }
    get persistenceData() {
        return {
            savedAt: Date.now(),
            state: {
                restaurantId: this.restaurant.id.value,
                items: this.payload.checkoutData.map((item) => {
                    return {
                        key: item.productKey,
                        note: item.note,
                        quantity: item.quantity,
                    };
                }),
                promotionalItems: this.promotionScheme.promotionInstances.raw.map((promotion) => {
                    if (promotion.kind === "WholeOrder") {
                        return {
                            //FIXME: hardcoded data, it need to be fixed
                            promotionId: promotion.id.value,
                            assignment: {
                                tag: "AssignmentToCart",
                            },
                            source: {
                                tag: "NoSource",
                            },
                            selection: {
                                tag: "AutoEnabled",
                            },
                            input: {
                                tag: "NoInput",
                            },
                        };
                    }
                    else if (promotion.kind === "Product") {
                        const contents = {
                            product: promotion.discountTarget.productInstance.checkoutData,
                            productSeq: promotion.discountTarget.checkoutData.productSequenceNum,
                        };
                        return {
                            //FIXME: hardcoded data, it need to be fixed
                            promotionId: promotion.id.value,
                            assignment: {
                                tag: "AssignmentToProduct",
                                contents,
                            },
                            source: {
                                tag: "NoSource",
                            },
                            selection: {
                                tag: "AutoEnabled",
                            },
                            input: {
                                tag: "NoInput",
                            },
                        };
                    }
                    else {
                        throw new Error("Illegal prmotion type");
                    }
                }),
                couponCode: null,
                extraDiscount: null, //FIXME: do we have extra discount available in this momement?
            },
        };
    }
    /**
     * @returns data to be sent to the backend
     */
    checkoutData(params) {
        const { customer, vatId } = this.orderReceiver.checkoutData;
        return {
            checkout: {
                restaurantId: this.restaurant.id.value,
                items: this.payload.checkoutData,
                locale: params.locale,
                ...this.fulfillmentInstructions.checkoutData,
                customer,
                origin: this.origin.checkoutData,
                extraDiscount: null,
                draft: null,
                vatId,
                packagingContainers: this.packaging.checkoutData,
                waiterPin: null,
                fieldTestExperiments: {},
                ...this.promotionScheme.checkoutData,
            },
            origin: params.appOrigin,
            payment: this.fulfillmentInstructions.paymentData,
            paymentChannel: null,
        };
    }
}
